// Turning a card into a phone contact.
//
// Tapping a tag already puts the card in this app's directory, but the place
// people actually look somebody up is their phone's own contacts — so a card
// that cannot get there is a card they have to remember to come back for. A
// .vcf is the one format every phone, on both platforms, opens into "add
// contact" without an app or an account.
//
// vCard 3.0 rather than 4.0 deliberately: iOS and Android both import 3.0
// reliably, while 4.0 support is uneven and the gain is nothing this card needs.
//
// The two rules that break real importers, both handled here: values must
// escape `\ ; ,` and newlines (RFC 2426 §5), and lines must be folded at 75
// OCTETS — bytes, not characters, which matters the moment a name is in Bangla
// and a photo is inlined.

/** One extra labelled phone or email, as the projection carries them. */
export interface VCardContact {
  label?: string;
  value?: string;
}

/** Everything a vCard can be built from. All optional — a card carrying only a
 *  name still produces a valid contact. */
export interface VCardInput {
  name?: string;
  title?: string;
  company?: string;
  phone?: string;
  email?: string;
  website?: string;
  address?: string;
  /** A data URL, as the public projection inlines it. */
  photo?: string;
  /** The card's own short link, kept on the contact so it can be reopened. */
  url?: string;
  extraPhones?: VCardContact[];
  extraEmails?: VCardContact[];
}

const CRLF = '\r\n';
const encoder = new TextEncoder();

/** Escape a value for vCard (RFC 2426 §5). Backslash first, or the escapes
 *  introduced below would themselves be escaped. */
function esc(value: string): string {
  return value
    .replace(/\\/g, '\\\\')
    .replace(/\r?\n/g, '\\n')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,');
}

/**
 * Fold one logical line to 75 octets per physical line, continuations starting
 * with a single space.
 *
 * Counted in BYTES and split on code points: folding by string length would
 * both under-fold Bangla (three bytes a character) and risk cutting a character
 * in half, which turns the whole contact into mojibake in an importer.
 */
function fold(line: string): string {
  if (encoder.encode(line).length <= 75) return line;
  const parts: string[] = [];
  let current = '';
  let bytes = 0;
  for (const ch of line) {
    const size = encoder.encode(ch).length;
    // The continuation's leading space counts toward its own 75.
    if (bytes + size > 75) {
      parts.push(current);
      current = ' ' + ch;
      bytes = 1 + size;
    } else {
      current += ch;
      bytes += size;
    }
  }
  parts.push(current);
  return parts.join(CRLF);
}

/** Split a display name into the structured N field's parts. A single `name`
 *  is all this product collects, so the last token is taken as the family name
 *  — a heuristic, which is why FN below carries the name exactly as written and
 *  is what every phone actually displays. */
function structuredName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return ';;;;';
  if (parts.length === 1) return `${esc(parts[0])};;;;`;
  const family = parts[parts.length - 1];
  const given = parts.slice(0, -1).join(' ');
  return `${esc(family)};${esc(given)};;;`;
}

/** Pull the media type and payload out of an inlined image. */
function photoLine(dataUrl: string): string | null {
  const m = /^data:image\/(jpeg|jpg|png|webp);base64,([A-Za-z0-9+/=]+)$/.exec(dataUrl.trim());
  if (!m) return null;
  const kind = m[1].toLowerCase() === 'jpg' ? 'JPEG' : m[1].toUpperCase();
  return `PHOTO;ENCODING=b;TYPE=${kind}:${m[2]}`;
}

/**
 * Build a vCard 3.0 document for one card.
 *
 * Returns a string with CRLF endings, ready to be handed to the OS as a
 * `text/vcard` file.
 */
export function toVCard(card: VCardInput): string {
  const name = (card.name ?? '').trim();
  const lines: string[] = ['BEGIN:VCARD', 'VERSION:3.0'];

  lines.push(`N:${structuredName(name)}`);
  // FN is required by the spec and is the field phones show, so it is never
  // omitted — an empty one still imports, a missing one can be rejected.
  lines.push(`FN:${esc(name)}`);

  if (card.company?.trim()) lines.push(`ORG:${esc(card.company.trim())}`);
  if (card.title?.trim()) lines.push(`TITLE:${esc(card.title.trim())}`);

  if (card.phone?.trim()) lines.push(`TEL;TYPE=CELL,VOICE:${esc(card.phone.trim())}`);
  for (const extra of card.extraPhones ?? []) {
    const value = extra.value?.trim();
    if (value) lines.push(`TEL;TYPE=VOICE:${esc(value)}`);
  }

  if (card.email?.trim()) lines.push(`EMAIL;TYPE=INTERNET,PREF:${esc(card.email.trim())}`);
  for (const extra of card.extraEmails ?? []) {
    const value = extra.value?.trim();
    if (value) lines.push(`EMAIL;TYPE=INTERNET:${esc(value)}`);
  }

  if (card.website?.trim()) lines.push(`URL:${esc(card.website.trim())}`);
  // The card's own link, so the contact leads back to the live card — which is
  // the whole point of a card that can be edited after it has been handed over.
  if (card.url?.trim() && card.url.trim() !== card.website?.trim()) {
    lines.push(`URL:${esc(card.url.trim())}`);
  }

  if (card.address?.trim()) {
    // The address is one free-text line here, so it goes in the street slot
    // rather than being guessed apart into city/region/postcode.
    lines.push(`ADR;TYPE=WORK:;;${esc(card.address.trim())};;;;`);
  }

  if (card.photo?.trim()) {
    const photo = photoLine(card.photo);
    if (photo) lines.push(photo);
  }

  lines.push('END:VCARD');
  return lines.map(fold).join(CRLF) + CRLF;
}

/** A filename for the download. Kept to characters every filesystem accepts,
 *  because this string reaches the OS.
 *
 *  `\p{M}` is in the keep-set alongside letters and numbers: Bengali writes its
 *  vowels as combining marks, which are Unicode category Mark and not Letter —
 *  so a letters-only filter silently strips the matras and রফিকুল arrives as
 *  রফ-ক-ল. Every Bangla name would have been mangled. */
export function vcardFilename(name: string): string {
  const safe = (name ?? '')
    .trim()
    .replace(/[^\p{L}\p{N}\p{M}]+/gu, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
  return `${safe || 'contact'}.vcf`;
}

/**
 * Hand a card to the operating system as a contact.
 *
 * A Blob and a download link, not a `data:` URL: top-level navigation to
 * `data:` is blocked by browsers, and the blob keeps the payload out of the URL
 * where a large inlined photo would not fit anyway.
 */
export function downloadVCard(card: VCardInput): void {
  const blob = new Blob([toVCard(card)], { type: 'text/vcard;charset=utf-8' });
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = vcardFilename(card.name ?? '');
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoked on the next turn of the loop: revoking synchronously can cancel the
  // download that was just started.
  setTimeout(() => URL.revokeObjectURL(href), 10_000);
}
